import {
  AthleteDraftWorkoutDomain,
  AthletePreview,
  BlockDraftWorkoutDomain,
  PublishedWorkoutStatusDomain,
  WorkoutLayerDomain,
  athleteDisplayName,
} from '@strength/domain';
import {
  MetricBenchmarkData,
  HasWorkoutLayer,
  Workout,
  mergeAthleteWorkoutFromLayers,
  mergeWorkoutLayersByWorkoutId,
} from '@strength/domain-workout';
import { tKey } from '@strength/i18n';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import { useMutation, useQuery, useQueryOne } from '../../../loco-sync';
import { trackEvent } from '../../../reporting/reporting';
import { trpc } from '../../../trpc/react';
import {
  DeleteConfirmationModal,
  deleteAthleteWorkoutText,
} from '../../components/modals/delete-confirmation-modal';
import { Sheet, useSheetDndSensors } from '../../components/sheet';
import { useViewer } from '../../context/viewer';
import { useBenchmarks } from '../../hooks/useBenchmarks';
import { useMetrics } from '../../hooks/useMetrics';
import { useToast } from '../../hooks/useToast';
import { jotaiStore } from '../../utils/jotai';
import { WorkoutTemplateDrawer } from '../library/workout-templates/components/WorkoutTemplateDrawer';
import {
  WorkoutTemplateData,
  WorkoutTemplateSelection,
  getNewMultiWorkoutSectionTemplate,
  getNewMultiWorkoutTemplate,
  getNewWorkoutTemplate,
  removeInstructionsFromLayers,
} from '../library/workout-templates/workout-template-utils';
import type {
  ApplySwapData,
  TrackExerciseSwapInfo,
} from './components/apply-swap-drawer';
import {
  ApplySwapDrawer,
  addRecentSwap,
  findMatchingAthletesForSwap,
  findMatchingExercisesForAthlete,
  findMatchingExercisesInFutureWorkouts,
  generateSwapCommandsForFutureInstances,
  recentSwapsAtom,
  removeRecentSwap,
} from './components/apply-swap-drawer';
import { getContextMenuContent } from './context-menu/context-menu';
import { WithWorkoutId } from './context-menu/data-for-context-menu';
import {
  collectMultiDayPasteCommandsByPosition,
  pasteCommands,
} from './copy-paste/copy-paste';
import { groupSelectionData } from './copy-paste/group-data';
import {
  buildWorkoutClipboard,
  workoutClipboardAtom,
} from './copy-paste/workout-clipboard';
import { handleDragEnd } from './dnd';
import { calculateSheetDimensions, getGridTemplateColumns } from './grid-utils';
import { useBlockExerciseVideos } from './hooks/useBlockExerciseVideos';
import { InstructionDrawer } from './instruction-drawer';
import { MiscMutationFns, WorkoutLayerCommand } from './mutation-fns/types';
import {
  commandsForBulkClear,
  findWorkoutLayer,
  makeWorkoutLayerMutationFns,
} from './mutation-fns/workout-draft';
import { CreateSchemePayload, NewSchemeDialog } from './new-scheme-dialog';
import { isPublishActionDisabled } from './publish/publish-state';
import {
  deletePublishTasks,
  usePublishErrorsForWorkouts,
} from './publish/utils';
import { createSchemeInput, selectSchemeCommands } from './scheme';
import { collapsedExercisesAtom, collapsedSectionsAtom } from './slots/common';
import { getSlotsForBlock } from './slots/workout';
import {
  AddSessionSide,
  BaseBlockSheetProps,
  BlockGroupBy,
  BlockSelectionData,
  InstructionEditData,
  SetGroupedData,
  WorkoutWithCycleAndSession,
} from './types';
import { useAthleteDraftWorkoutUndoRedo } from './undo-redo/athlete';
import {
  getDisplayNameForWorkout,
  mergeWorkoutsWithCyclesAndSessions,
} from './utils';
import {
  resolveCreateSlot,
  runAthleteMutations as runMutations,
} from './mutation-fns/run-mutations';
import { getWorkoutStatusesForAthlete } from './workout-statuses';
import { ConfirmationModal } from '../../components/modals/confirmation-modal';
import {
  applyMultiWorkoutTemplateActualsText,
  applyMultiWorkoutTemplateDraftOverwriteText,
  applyMultiWorkoutTemplateOverflowText,
} from './apply-multi-workout-template-text';
import {
  selectMultiWorkoutTemplateCommands,
  selectWorkoutTemplateCommands,
  MultiWorkoutTemplateApplyPlan,
} from './workout-template';
import { BlockAllView } from './block-all/BlockAllView';
import { SingleWorkoutEditContext } from './block-all/SingleWorkoutSheet';
import {
  getCycleForDate,
  getDateOrderedInsertPosition,
} from './block-all/calendar-utils';
import { DragEndEvent } from '@dnd-kit/core';

export type AthleteSheetProps = {
  selectedAthlete: AthletePreview;
  selectedWorkoutId?: string;
  showActuals: boolean;
  groupBy?: BlockGroupBy;
} & BaseBlockSheetProps;

export function AthleteSheet({
  block,
  hasBlockBeenPublished,
  publishState,
  blockWorkoutLayers,
  actualWorkoutLayers,
  blockPublishedWorkoutLayers,
  athletePublishedWorkoutLayers,
  modifiedPublishedWorkoutLayers,
  selectedAthlete,
  view,
  focusOptions,
  workoutTemplateTagOptions,
  selectedWorkoutId,
  showActuals,
  groupBy,
  readOnly,
}: AthleteSheetProps) {
  // SC-5486: distance-constrained activation so the sheet's click-drag cell
  // selection and dnd-kit's drag share the same pointer gesture without
  // fighting (plain clicks select, a move past 8px drags).
  const sheetSensors = useSheetDndSensors();
  const { id: profileId } = selectedAthlete;
  const { t } = useTranslation();
  const {
    navbar: { navbarTopColor, navbarBottomColor },
    team: { selectedTeam },
    org: {
      settings: { enableTeamLibrary },
    },
    hasPermissions,
  } = useViewer();
  const canCreateScheme = hasPermissions('library.schemes', 'create');
  const canCreateTemplate = hasPermissions('library.templates', 'create');
  const { defaultBenchmarkGridId } = useBenchmarks();
  const brandColors = { navbarBottomColor, navbarTopColor };

  const [modalOrDrawerOpen, setModalOrDrawerOpen] = useState(false);
  // Write-only subscription (SC-5564): the sheet no longer re-renders when a
  // copy updates the clipboard; paste reads the current value via jotaiStore.
  const setWorkoutClipboard = useSetAtom(workoutClipboardAtom);
  // Keep the atom mounted for the sheet's lifetime (SC-5564 review): the
  // subscription drives storage hydration and cross-tab sync without the
  // component re-rendering on value changes.
  useEffect(() => jotaiStore.sub(workoutClipboardAtom, () => {}), []);
  const [applySwapData, setApplySwapData] = useState<ApplySwapData | null>(
    null,
  );
  const [instructionEditData, setInstructionEditData] =
    useState<null | InstructionEditData>(null);
  const [workoutTemplateData, setWorkoutTemplateData] =
    useState<null | WorkoutTemplateData>(null);
  const [newSchemeData, setNewSchemeData] = useState<
    WithWorkoutId<SetGroupedData>[] | null
  >(null);
  // A multi-workout template apply gated behind overflow / draft-overwrite
  // warnings: the generated plan is held here until all warnings are accepted.
  const [pendingMultiApply, setPendingMultiApply] = useState<{
    plan: MultiWorkoutTemplateApplyPlan;
    template: WorkoutTemplateData;
    warning: 'overflow' | 'draftOverwrite' | 'actuals' | null;
  } | null>(null);
  const [sessionToDelete, setSessionToDelete] = useState<{
    cycleSortOrder: number;
    sessionSortOrder: number;
  } | null>(null);

  // In-memory new draft workout for an All-view "add workout" in progress
  // (athlete layer). Nothing is persisted until the first real edit; the
  // athlete layout's insert-and-bump (getWorkoutLayout) handles sibling
  // ordering, so unlike the base block there is no explicit bump to persist.
  const [newDraftWorkout, setNewDraftWorkout] =
    useState<WorkoutWithCycleAndSession | null>(null);

  const isPublishDisabled = isPublishActionDisabled(publishState);
  const [workoutToCancel, setWorkoutToCancel] = useState<string | null>(null);
  const [workoutToDelete, setWorkoutToDelete] = useState<string | null>(null);
  const { getMetric, getMetrics, setsMetric } = useMetrics();
  const metrics = useMemo(() => {
    return getMetrics(true);
  }, [getMetrics]);

  // Tombstoned layers (deletedAt set) must stay in this query — the grid
  // layout builder uses them to hide block-layer workouts that were deleted
  // for this athlete.
  const { data: athleteWorkoutLayers } = useQuery('AthleteDraftWorkoutLayer', {
    blockId: block.id,
    profileId,
  });

  // Queries for bulk exercise swap feature
  const { data: allAthleteDraftWorkoutLayers = [] } = useQuery(
    'AthleteDraftWorkoutLayer',
    { blockId: block.id, deletedAt: null },
  );
  const blockForDraftQuery = trpc.block.blockForDraft.useQuery({
    blockId: block.id,
  });
  const allAthletes = useMemo(
    () => blockForDraftQuery.data?.athletes ?? [],
    [blockForDraftQuery.data?.athletes],
  );

  const athleteExerciseBenchmarksResult =
    trpc.athleteBenchmarks.fetchAllExerciseBenchmarksForAthlete.useQuery(
      {
        profileId,
      },
      {
        staleTime: 10_000,
      },
    );
  const currentExerciseBenchmarks = athleteExerciseBenchmarksResult.data;

  // Fetch when team library is disabled (no scoping needed) or when a team is available (scoped query)
  // Falls back to block.teamId if no team is selected to avoid disabling the query
  const effectiveTeamId = selectedTeam || block.teamId;
  const { data: maxConversions } =
    trpc.exerciseMaxConversionLibrary.allExerciseMaxConversions.useQuery(
      undefined,
      { enabled: !enableTeamLibrary || !!effectiveTeamId, staleTime: 10_000 },
    );
  // KPI-mapped metric columns resolve their `%` to this athlete's per-athlete
  // targets (SC-3838).
  const { data: kpiBenchmarks } =
    trpc.athleteKpiBenchmarks.fetchKpiBenchmarksForAthlete.useQuery(
      { profileId },
      { staleTime: 10_000 },
    );

  const { undo, redo, bulkMutate } = useAthleteDraftWorkoutUndoRedo(
    block.id,
    profileId,
  );

  const { toast } = useToast();

  // if there is an actual workout layer for the athlete
  // we want to merge the athlete layer with the published block layer associated with the actual instead of the draft block layer
  // because the draft layer could have been edited
  // Pre-index published layers for O(1) lookups
  // Build indexes once for fast lookups
  const actualByWorkoutId = useMemo(() => {
    const m = new Map<string, (typeof actualWorkoutLayers)[number]>();
    for (const a of actualWorkoutLayers) m.set(a.workoutId, a);
    return m;
  }, [actualWorkoutLayers]);
  const athletePubByKey = useMemo(() => {
    const m = new Map<string, (typeof athletePublishedWorkoutLayers)[number]>();
    for (const p of athletePublishedWorkoutLayers)
      m.set(`${p.workoutId}:${p.version}`, p);
    return m;
  }, [athletePublishedWorkoutLayers]);
  const blockPubByKey = useMemo(() => {
    const m = new Map<string, (typeof blockPublishedWorkoutLayers)[number]>();
    for (const p of blockPublishedWorkoutLayers)
      m.set(`${p.workoutId}:${p.version}`, p);
    return m;
  }, [blockPublishedWorkoutLayers]);

  // Helper function to get published layer data if conditions are met
  const getPublishedLayerData = (
    workoutId: string,
    useBlockPublished: boolean,
  ) => {
    const actual = actualByWorkoutId.get(workoutId);
    if (!actual) return null;
    const key = useBlockPublished
      ? `${workoutId}:${actual.blockVersion}`
      : `${workoutId}:${actual.athleteVersion}`;
    return (
      (useBlockPublished ? blockPubByKey : athletePubByKey).get(key) ?? null
    );
  };

  // Modify layers with published layer based on actual data
  const modifyLayer = <T extends HasWorkoutLayer>(
    layer: T,
    useBlockPublished: boolean,
  ): T => {
    const publishedData = getPublishedLayerData(
      layer.workoutId,
      useBlockPublished,
    );
    return publishedData
      ? {
          ...layer,
          assignedDate: publishedData.assignedDate,
          layer: publishedData.layer,
        }
      : layer;
  };

  // showActuals = OFF, use draft layers for editing
  // showActuals = ON, use published layers
  const modifiedBlockWorkoutLayers = showActuals
    ? blockWorkoutLayers.map((layer) => modifyLayer(layer, true))
    : blockWorkoutLayers;
  const modifiedAthleteWorkoutLayers = showActuals
    ? athleteWorkoutLayers.map((layer) => modifyLayer(layer, false))
    : athleteWorkoutLayers;

  const workoutStatuses = getWorkoutStatusesForAthlete(
    modifiedBlockWorkoutLayers,
    modifiedAthleteWorkoutLayers,
    athletePublishedWorkoutLayers,
    actualWorkoutLayers,
    blockPublishedWorkoutLayers,
  );

  // Render read-only, already-published workouts from the current published
  // layer (sets expanded, no Sets metric) instead of the draft layer (which
  // still carries the unexpanded Sets metric).
  // - Canceled workouts have no actuals, so always use the published layer.
  // - Completed workouts show their actuals via the Show Actuals toggle; when
  //   it's off, still render the expanded published prescription rather than
  //   the collapsed draft.
  // - Cross-team readonly viewers (readOnly=true) see the published prescription
  //   so they get the fully expanded set rows rather than the Sets metric
  //   shorthand. Falls back to the draft when no published layer exists yet.
  const shouldRenderFromPublished = (workoutId: string) => {
    const type = workoutStatuses.find((s) => s.workoutId === workoutId)?.status
      .type;
    // Canceled workouts always show the published prescription regardless of
    // showActuals — canceled workouts cannot have actuals recorded against them,
    // so there is no actuals-backed layer that this could interfere with.
    if (type === 'canceled') return true;
    if (!showActuals && type === 'completed') return true;
    return readOnly && !showActuals;
  };
  const displayBlockLayers = modifiedBlockWorkoutLayers.map((layer) => {
    if (!shouldRenderFromPublished(layer.workoutId)) return layer;
    const published = blockPublishedWorkoutLayers.find(
      (p) => p.workoutId === layer.workoutId && p.isCurrent,
    );
    return published ? { ...layer, layer: published.layer } : layer;
  });
  const displayAthleteLayers = modifiedAthleteWorkoutLayers.map((layer) => {
    if (!shouldRenderFromPublished(layer.workoutId)) return layer;
    const published = athletePublishedWorkoutLayers.find(
      (p) =>
        p.workoutId === layer.workoutId &&
        p.profileId === profileId &&
        p.isCurrent,
    );
    return published ? { ...layer, layer: published.layer } : layer;
  });

  const workouts = mergeWorkoutsWithCyclesAndSessions(
    block,
    blockWorkoutLayers,
    mergeWorkoutLayersByWorkoutId(
      displayBlockLayers,
      displayAthleteLayers,
      // Only include modifiedPublishedWorkoutLayers when showing actuals
      ...(showActuals ? [modifiedPublishedWorkoutLayers] : []),
    ),
    actualWorkoutLayers,
    modifiedPublishedWorkoutLayers,
    workoutStatuses,
    modifiedAthleteWorkoutLayers as typeof athleteWorkoutLayers,
  );
  useBlockExerciseVideos(workouts);

  // Workouts the All view (and its create path) sees: real workouts plus any
  // in-progress add stub, so the create path can read its mapped cycle, sort
  // order, and pre-filled date when the first edit persists it.
  const allViewWorkouts = newDraftWorkout
    ? [...workouts, newDraftWorkout]
    : workouts;

  // If `workoutId` is the pending new draft workout, clear it on its first real
  // edit. The athlete draft layer is created by the normal mutation path (which
  // reads the draft from `allViewWorkouts`); getWorkoutLayout handles sibling
  // ordering, so there is nothing extra to persist here. No-op for any other
  // workout id.
  function commitNewDraftWorkout(workoutId: string) {
    if (newDraftWorkout && newDraftWorkout.workoutId === workoutId) {
      setNewDraftWorkout(null);
    }
  }

  const { data: publishTask } = useQueryOne('BlockPublishTask', {
    blockId: block.id,
  });
  const workoutPublishErrors = usePublishErrorsForWorkouts(
    workouts,
    publishTask,
    (workout) => {
      const blockWorkout = blockWorkoutLayers.find(
        (e) => e.workoutId === workout.workoutId,
      );
      if (!blockWorkout && !workout.assignedDate) {
        return new Map([['assignedDate', [tKey('assignedDateIsRequired')]]]);
      }
      return undefined;
    },
  );

  const rawMutation = useMutation();
  const mutationFns = makeWorkoutLayerMutationFns(
    'AthleteDraftWorkoutLayer',
    athleteWorkoutLayers,
    (command) => runCommands([command]),
    setsMetric,
    blockWorkoutLayers,
  );

  const createScheme = trpc.schemeLibrary.createScheme.useMutation();
  const onCreateScheme = (schemePayload: CreateSchemePayload) => {
    const result = createSchemeInput(
      block,
      workouts,
      focusOptions,
      schemePayload,
      getMetric,
    );
    if (result.ok) {
      createScheme.mutate(result.value, {
        onSuccess: () =>
          toast({
            title: t('scheme.created', { schemeName: result.value.name }),
            severity: 'success',
          }),
      });
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  };

  const onSelectScheme: MiscMutationFns['onSelectScheme'] = (
    section,
    exercise,
    scheme,
  ) => {
    const result = selectSchemeCommands(
      block,
      workouts,
      athleteWorkoutLayers,
      section,
      exercise,
      scheme,
      'AthleteDraftWorkoutLayer',
      setsMetric,
      blockWorkoutLayers,
    );
    if (result.ok) {
      runCommands(result.value);
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  };

  const onSetWorkoutTemplate = (
    workoutId: string,
    selection: WorkoutTemplateSelection,
  ) => {
    // get name for the workout
    const workout = workouts.find((w) => w.workoutId === workoutId);
    if (!workout) {
      return;
    }
    const name = workout.name ?? getDisplayNameForWorkout(workout);

    const layers = [
      ...blockWorkoutLayers
        .filter((l) => l.workoutId === workoutId)
        .map((l) => l.layer),
      ...athleteWorkoutLayers
        .filter((l) => l.workoutId === workoutId)
        .map((l) => l.layer),
    ];
    if (layers.length === 0) {
      return;
    }
    const template = getNewWorkoutTemplate(name, selection, layers);
    setWorkoutTemplateData(template);
  };

  const onSetMultiWorkoutTemplate = (
    selection: string[] | { workoutId: string; sectionIds: string[] }[],
  ) => {
    // section-level selections come from highlighting sections across days;
    // string[] comes from whole-day selections
    const isSectionSelection = selection.some((s) => typeof s !== 'string');
    const perDay: { workoutId: string; sectionIds: string[] }[] =
      isSectionSelection
        ? (selection as { workoutId: string; sectionIds: string[] }[])
        : (selection as string[]).map((workoutId) => ({
            workoutId,
            sectionIds: [],
          }));

    if (perDay.length < 2) {
      // single-day selections route to the existing single-workout flow
      const [day] = perDay;
      if (day) {
        onSetWorkoutTemplate(
          day.workoutId,
          day.sectionIds.length === 0 ? 'all' : day.sectionIds,
        );
      }
      return;
    }
    const workoutIds = perDay.map(({ workoutId }) => workoutId);
    const selectedWorkouts = workoutIds
      .map((id) => workouts.find((w) => w.workoutId === id))
      .filter((w) => !!w);
    if (selectedWorkouts.length < 2) {
      return;
    }
    const first = selectedWorkouts[0];
    const name = first
      ? (first.name ?? getDisplayNameForWorkout(first))
      : t('defaultTemplateName');
    const layersByWorkout = new Map(
      workoutIds.map((id) => [
        id,
        [
          ...blockWorkoutLayers
            .filter((l) => l.workoutId === id)
            .map((l) => l.layer),
          ...athleteWorkoutLayers
            .filter((l) => l.workoutId === id)
            .map((l) => l.layer),
        ],
      ]),
    );
    setWorkoutTemplateData(
      isSectionSelection
        ? getNewMultiWorkoutSectionTemplate(
            name,
            selection as { workoutId: string; sectionIds: string[] }[],
            layersByWorkout,
          )
        : getNewMultiWorkoutTemplate(name, workoutIds, layersByWorkout),
    );
  };

  const createWorkoutTemplate =
    trpc.workoutTemplateLibrary.createWorkoutTemplate.useMutation();
  const onSaveWorkoutTemplate = (
    workoutTemplate: WorkoutTemplateData,
    saveInstructions: boolean,
  ) => {
    const { name, tags, template, id, isMultiDay } = workoutTemplate;
    let processedLayers = template;
    if (!saveInstructions) {
      processedLayers = removeInstructionsFromLayers(template);
    }
    const tagSelections = tags.map((tag, i) => ({
      id: v4(),
      sortOrder: i,
      tag,
    }));
    createWorkoutTemplate.mutate(
      {
        id,
        name,
        tags: tagSelections,
        template: processedLayers,
        isMultiDay,
      },
      {
        onSuccess: () =>
          toast({
            title: t('workoutTemplateAdded', { name }),
            severity: 'success',
          }),
        onError: () =>
          toast({
            title: t('somethingWentWrong'),
            severity: 'error',
          }),
      },
    );
  };

  function recomputeDraftWithDate(
    prev: WorkoutWithCycleAndSession,
    assignedDate: string | null,
  ): WorkoutWithCycleAndSession {
    if (!assignedDate) return { ...prev, assignedDate };
    const cycle = getCycleForDate(assignedDate, block.sortedCycles);
    if (!cycle) return { ...prev, assignedDate };
    const cycleSessions = workouts
      .filter((w) => w.workoutId !== prev.workoutId && w.cycle.id === cycle.id)
      .map((w) => ({
        workoutId: w.workoutId,
        sessionSortOrder: w.session.id,
        assignedDate: getAssignedDate(w) ?? w.assignedDate,
      }));
    const { sessionSortOrder } = getDateOrderedInsertPosition(
      cycleSessions,
      assignedDate,
    );
    return {
      ...prev,
      assignedDate,
      cycle,
      session: {
        id: sessionSortOrder,
        name: `${block.sessionLabel} ${sessionSortOrder}`,
      },
    };
  }

  const onEditWorkoutDate: MiscMutationFns['onEditWorkoutDate'] = (
    workoutId,
    assignedDate,
  ) => {
    // Changing the pre-filled date on a not-yet-saved draft is NOT a real edit
    // — update it locally so a later content edit persists the right slot.
    if (newDraftWorkout && newDraftWorkout.workoutId === workoutId) {
      setNewDraftWorkout((prev) =>
        prev ? recomputeDraftWithDate(prev, assignedDate) : prev,
      );
      return;
    }

    const edits: AthleteDraftWorkoutDomain.Edit[] = [];
    // SC-5512: resolution is by workout id only — an empty-slot cell's key
    // is not a workout id, so it resolves to nothing and the create branch
    // below builds a fresh athlete layer at the key's slot.
    const { workoutLayer, identity } = findWorkoutLayer(
      athleteWorkoutLayers,
      workoutId,
      'AthleteDraftWorkoutLayer',
    );
    const excludeAssignedDate =
      assignedDate === null && block.allowFlexWorkouts;
    // SC-5512: a live block-backed workout keeps its id (the athlete layer
    // links to it); a create uses the shared linked id minted below.
    let athleteCommandWorkoutId = identity.workoutId;
    if (workoutLayer) {
      edits.push({
        modelName: 'AthleteDraftWorkoutLayer',
        modelId: workoutLayer.id,
        action: 'update',
        data: { assignedDate, excludeAssignedDate },
      });
    } else {
      const { workoutLayer: blockLayer, identity: blockIdentity } =
        findWorkoutLayer(
          blockWorkoutLayers,
          workoutId,
          'BlockDraftWorkoutLayer',
        );
      // SC-5512: the created athlete layer links to the block layer by id —
      // when a block row already exists for this workout (found by id), its
      // id is reused so the rows merge as an overlay; otherwise ONE fresh id
      // is minted and shared by both creates. Never the empty-slot cell key
      // (it is not a workout id).
      const linkedWorkoutId = blockLayer ? blockIdentity.workoutId : v4();
      athleteCommandWorkoutId = linkedWorkoutId;
      // SC-5512: the create carries its slot explicitly from the cell —
      // never a silent slot-0 fallback.
      const { cycleSortOrder, sessionSortOrder } = resolveCreateSlot(
        workoutId,
        allViewWorkouts,
      );

      // The user set the assigned date on a non-existant athlete layer,
      // so we need to generate the block layer too in order to link them
      if (!blockLayer) {
        rawMutation.mutate({
          module: BlockDraftWorkoutDomain.Module,
          identity: { workoutId: linkedWorkoutId, blockId: block.id },
          edits: [
            {
              modelName: 'BlockDraftWorkoutLayer',
              modelId: v4(),
              action: 'create',
              data: {
                cycleSortOrder,
                sessionSortOrder,
                layer: WorkoutLayerDomain.defaultLayer(),
                name: null,
              },
            },
          ],
        });
      }

      edits.push({
        modelName: 'AthleteDraftWorkoutLayer',
        modelId: v4(),
        action: 'create',
        data: {
          assignedDate,
          excludeAssignedDate,
          cycleSortOrder,
          sessionSortOrder,
          layer: WorkoutLayerDomain.defaultLayer(),
          name: null,
        },
      });
    }

    bulkMutate([
      {
        module: AthleteDraftWorkoutDomain.Module,
        identity: {
          workoutId: athleteCommandWorkoutId,
          blockId: block.id,
          profileId,
        },
        edits,
      },
    ]);
  };

  const onEditWorkoutName: MiscMutationFns['onEditWorkoutName'] = (
    workoutId,
    name,
  ) => {
    // SC-5512: slot-aware resolution — empty-slot cells have no workout id.
    // The returned identity carries the bound row's real id, or a minted
    // model id with exists=false (the create path mints the identity).
    const { identity } = findWorkoutLayer(
      athleteWorkoutLayers,
      workoutId,
      'AthleteDraftWorkoutLayer',
    );
    const workoutLayerId = identity.id;
    const modelName = 'AthleteDraftWorkoutLayer';
    runCommands([
      {
        identity,
        edits: [
          {
            modelName,
            modelId: workoutLayerId,
            action: 'update',
            data: {
              name: name ?? null,
            },
          },
        ],
      },
    ]);
  };

  const onAddSession: MiscMutationFns['onAddSession'] = (
    cycleSortOrder: number,
    sessionSortOrder: number,
    side: AddSessionSide,
  ) => {
    const edits: AthleteDraftWorkoutDomain.Edit[] = [
      {
        modelName: 'AthleteDraftWorkoutLayer',
        modelId: v4(),
        action: 'create',
        data: {
          cycleSortOrder,
          sessionSortOrder: sessionSortOrder + (side === 'add-right' ? 1 : 0),
          layer: WorkoutLayerDomain.defaultLayer(),
          name: null,
        },
      },
    ];

    const commands = [
      {
        module: AthleteDraftWorkoutDomain.Module,
        identity: {
          workoutId: v4(),
          blockId: block.id,
          profileId,
        },
        edits,
      },
    ];
    bulkMutate(commands);
  };

  const doSessionDelete = ({
    cycleSortOrder,
    sessionSortOrder,
  }: {
    cycleSortOrder: number;
    sessionSortOrder: number;
  }) => {
    const athleteWorkoutLayer = athleteWorkoutLayers.find(
      (e) =>
        e.cycleSortOrder === cycleSortOrder &&
        e.sessionSortOrder === sessionSortOrder,
    );
    const blockWorkoutLayer = blockWorkoutLayers.find((b) => {
      if (athleteWorkoutLayer) {
        return b.workoutId === athleteWorkoutLayer?.workoutId;
      } else {
        return (
          b.cycleSortOrder === cycleSortOrder &&
          b.sessionSortOrder === sessionSortOrder
        );
      }
    });

    // scenarios:
    // 1. block layer exists, athlete layer exists
    //    - create delete for athlete layer
    // 2. block layer does not exist, athlete layer exists
    //    - create delete for athlete layer
    // 3. block layer exists, athlete layer does not exist
    //    - create athlete layer with deletedAt set
    // 4. block layer does not exist, athlete layer does not exist
    //    - create athlete layer with deletedAt set

    let edit: AthleteDraftWorkoutDomain.Edit;
    let id = athleteWorkoutLayer?.id;
    if (!id) {
      id = v4();
      edit = {
        modelName: 'AthleteDraftWorkoutLayer',
        modelId: id,
        action: 'create',
        data: {
          cycleSortOrder,
          sessionSortOrder: sessionSortOrder,
          layer: WorkoutLayerDomain.defaultLayer(),
          deletedAt: DateTime.now().toISO(),
          name: null,
        },
      };
    } else {
      edit = {
        modelName: 'AthleteDraftWorkoutLayer',
        modelId: id,
        action: 'delete',
      };
    }

    const commands = [
      {
        module: AthleteDraftWorkoutDomain.Module,
        identity: {
          workoutId:
            // if the block layer exists, then this isn't an athlete only workout
            blockWorkoutLayer?.workoutId ??
            // removing an existing athlete only workout
            athleteWorkoutLayer?.workoutId ??
            // creating new athlete only workout in deleted state
            v4(),
          blockId: block.id,
          profileId,
        },
        edits: [edit],
      },
    ];
    bulkMutate(commands);
  };

  const onRemoveSession: MiscMutationFns['onRemoveSession'] = (
    cycleSortOrder,
    sessionSortOrder,
  ) => {
    setSessionToDelete({ cycleSortOrder, sessionSortOrder });
  };

  const doWorkoutCancel = (workoutId: string) => {
    // if publish is in progress don't allow cancel
    if (isPublishDisabled) {
      toast({
        title: t('publishInProgress'),
        severity: 'error',
      });
      return;
    }

    // delete any related publish tasks before canceling
    if (publishState.type === 'stalled') {
      deletePublishTasks(publishState.task, rawMutation.mutate);
    }

    // Find the relevant layers for this workout and athlete. The actuals layer
    // is optional — it only exists once the athlete has started the workout.
    // When actuals exist we merge against the published versions they were
    // recorded against; otherwise we fall back to the current published
    // prescription. (Previously this required an actuals row and threw "Failed
    // to merge workout layers" when canceling a workout the athlete hadn't
    // started.)
    const actual = actualByWorkoutId.get(workoutId);
    const athletePublishedLayer = actual
      ? athletePubByKey.get(`${workoutId}:${actual.athleteVersion}`)
      : athletePublishedWorkoutLayers.find(
          (p) =>
            p.workoutId === workoutId &&
            p.profileId === profileId &&
            p.isCurrent,
        );
    const blockPublishedLayer = actual
      ? blockPubByKey.get(`${workoutId}:${actual.blockVersion}`)
      : blockPublishedWorkoutLayers.find(
          (b) =>
            b.workoutId === workoutId &&
            (athletePublishedLayer
              ? b.version === athletePublishedLayer.blockVersion
              : b.isCurrent),
        );
    const modifiedPublishedLayer = modifiedPublishedWorkoutLayers.find(
      (layer) => layer.workoutId === workoutId && layer.profileId === profileId,
    );
    const actualLayer = actualWorkoutLayers.find(
      (layer) => layer.workoutId === workoutId && layer.profileId === profileId,
    );
    const workout = mergeAthleteWorkoutFromLayers(
      workoutId,
      blockPublishedLayer?.layer,
      athletePublishedLayer?.layer,
      modifiedPublishedLayer?.layer,
      actualLayer?.layer,
    );

    if (!workout) {
      throw new Error('Failed to merge workout layers');
    }

    const command: PublishedWorkoutStatusDomain.Command = {
      module: PublishedWorkoutStatusDomain.Module,
      type: 'UpdateAthleteWorkoutStatus',
      input: {
        blockId: block.id,
        workoutId,
        timezone: DateTime.local().zoneName,
        updates: [
          {
            profileId,
            status: 'canceled',
            workout,
          },
        ],
      },
    };

    rawMutation.mutate(command);

    trackEvent('Strength | Block Builder | Cancel Athlete Workout', {
      blockId: block.id,
    });
    toast({
      title: t('success'),
      description: t('workoutCanceled'),
      severity: 'success',
    });
  };

  const onCancelWorkout: MiscMutationFns['onCancelWorkout'] = (
    workoutId: string,
  ) => {
    setWorkoutToCancel(workoutId);
  };

  const onDeleteWorkout: MiscMutationFns['onDeleteWorkout'] = (
    workoutId: string,
  ) => {
    setWorkoutToDelete(workoutId);
  };

  const onSelectWorkoutTemplate: MiscMutationFns['onSelectWorkoutTemplate'] = (
    workoutToPasteInto: Workout,
    workoutTemplate: WorkoutTemplateData,
  ) => {
    // Multi-day apply maps days onto consecutive slots of the *visible* view,
    // following the same order the sheet renders. The All view has no
    // cycle/session columns to traverse, so it keeps the single-day flow.
    // Templates loaded from the library don't carry isMultiDay (the column
    // isn't persisted), so infer it from the day count.
    const isMultiDayTemplate =
      workoutTemplate.isMultiDay ?? workoutTemplate.template.length > 1;
    if (isMultiDayTemplate && (groupBy === 'session' || groupBy === 'cycle')) {
      // visible workouts only — traversal (and overflow) stops at the view
      // boundary, matching the multi-day paste helpers
      const orderedWorkouts = [...displayWorkouts].sort((a, b) =>
        groupBy === 'session'
          ? a.cycle.startDate.localeCompare(b.cycle.startDate)
          : a.session.id - b.session.id,
      );
      const result = selectMultiWorkoutTemplateCommands({
        orderedWorkouts,
        startWorkoutId: workoutToPasteInto.workoutId,
        template: workoutTemplate,
        blockWorkoutLayers: athleteWorkoutLayers,
        // clearing an athlete layer must emit removals for block-draft
        // sections too, so the block prescription doesn't show through
        baseLayers: blockWorkoutLayers,
        modelName: 'AthleteDraftWorkoutLayer',
        setsMetric,
      });
      if (!result.ok) {
        toast({ title: t('pasteOperationError'), severity: 'error' });
        return;
      }
      const { overflow, draftWorkoutIds, actualsDayNumbers, commands } =
        result.value;
      // actuals days block the whole apply — nothing commits while any target
      // day has logged Actuals
      if (actualsDayNumbers.length > 0) {
        setPendingMultiApply({
          plan: result.value,
          template: workoutTemplate,
          warning: 'actuals',
        });
        return;
      }
      if (!overflow && draftWorkoutIds.length === 0) {
        // no warnings — commit straight away
        runCommands(commands);
        return;
      }
      setPendingMultiApply({
        plan: result.value,
        template: workoutTemplate,
        warning: overflow ? 'overflow' : 'draftOverwrite',
      });
      return;
    }

    const result = selectWorkoutTemplateCommands(
      workoutToPasteInto,
      athleteWorkoutLayers,
      workoutTemplate,
      'AthleteDraftWorkoutLayer',
      setsMetric,
    );
    if (result.ok) {
      runCommands(result.value);
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  };
  function runCommands(commands: WorkoutLayerCommand[]) {
    // A create targeting the pending new draft is its first real edit — clear
    // the draft and carry its pre-filled date onto the new athlete layer.
    for (const command of commands) {
      if (!command.identity.exists) {
        commitNewDraftWorkout(command.identity.workoutId);
      }
    }
    runMutations(
      commands,
      block.id,
      profileId,
      allViewWorkouts,
      rawMutation.mutate,
      bulkMutate,
      newDraftWorkout && newDraftWorkout.assignedDate
        ? {
            workoutId: newDraftWorkout.workoutId,
            assignedDate: newDraftWorkout.assignedDate,
          }
        : undefined,
    );
  }

  const confirmPendingMultiApply = () => {
    if (!pendingMultiApply) return;
    const { plan } = pendingMultiApply;
    // the actuals warning blocks the apply entirely — confirming just closes
    if (pendingMultiApply.warning === 'actuals') {
      setPendingMultiApply(null);
      return;
    }
    // advance to the next warning or commit
    if (
      pendingMultiApply.warning === 'overflow' &&
      plan.draftWorkoutIds.length > 0
    ) {
      setPendingMultiApply({ ...pendingMultiApply, warning: 'draftOverwrite' });
      return;
    }
    setPendingMultiApply(null);
    runCommands(plan.commands);
  };

  const displayWorkouts = workouts.filter((w) =>
    view.mode === 'session'
      ? w.session.id === view.sessionId
      : w.cycle.id === view.cycleId,
  );

  const collapsedSections = useAtomValue(collapsedSectionsAtom);
  const collapsedExercises = useAtomValue(collapsedExercisesAtom);
  const {
    maxSetsColumns,
    maxNonSetsColumns,
    greatestExerciseHeightsInSections,
    maxMetricsPerExercise,
  } = useMemo(() => {
    return calculateSheetDimensions(
      displayWorkouts,
      profileId,
      block.status,
      { collapsedSections, collapsedExercises },
      showActuals,
    );
  }, [
    displayWorkouts,
    profileId,
    block.status,
    showActuals,
    collapsedSections,
    collapsedExercises,
  ]);

  const applySwapMatchingAthletes = useMemo(() => {
    if (!applySwapData) return [];
    return findMatchingAthletesForSwap(
      allAthletes,
      blockWorkoutLayers,
      allAthleteDraftWorkoutLayers,
      profileId,
      applySwapData.oldLibraryExerciseId,
    );
  }, [
    applySwapData,
    allAthletes,
    blockWorkoutLayers,
    allAthleteDraftWorkoutLayers,
    profileId,
  ]);

  const miscMutationFns: MiscMutationFns = {
    onSelectScheme,
    onEditWorkoutDate,
    onEditWorkoutName,
    onAddSession,
    onRemoveSession,
    onCancelWorkout,
    onDeleteWorkout,
    onSelectWorkoutTemplate,
  };

  const createNewDraftWorkout = (
    date: string,
  ): WorkoutWithCycleAndSession | null => {
    const cycle = getCycleForDate(date, block.sortedCycles);
    if (!cycle) {
      return null;
    }
    const cycleSessions = workouts
      .filter((w) => w.cycle.id === cycle.id)
      .map((w) => ({
        workoutId: w.workoutId,
        sessionSortOrder: w.session.id,
        assignedDate: getAssignedDate(w) ?? w.assignedDate,
      }));
    const { sessionSortOrder } = getDateOrderedInsertPosition(
      cycleSessions,
      date,
    );
    const workout: WorkoutWithCycleAndSession = {
      workoutId: v4(),
      sections: [],
      exists: false,
      actual: undefined,
      modifiedPublished: undefined,
      assignedDate: date,
      status: { type: 'draft' },
      session: {
        id: sessionSortOrder,
        name: `${block.sessionLabel} ${sessionSortOrder}`,
      },
      cycle,
      isAthleteOnlyLayer: true,
      name: null,
    };
    setNewDraftWorkout(workout);
    return workout;
  };

  const cancelNewDraftWorkout = () => setNewDraftWorkout(null);

  const getAssignedDate = (workout: WorkoutWithCycleAndSession) => {
    // If the coach has cleared the date in the draft but this athlete has
    // already completed the workout against a published date, fall back to
    // that published date so historical completion context isn't erased in
    // the coach's athlete view.
    const publishedLayer = athletePublishedWorkoutLayers.find(
      (l) =>
        l.workoutId === workout.workoutId &&
        l.profileId === profileId &&
        l.isCurrent,
    );
    if (publishedLayer?.status === 'completed' && publishedLayer.assignedDate) {
      return publishedLayer.assignedDate;
    }
    // otherwise don't try to auto assign dates for athlete workouts
    return null;
  };

  // Sheet interaction handlers, lifted out of the JSX so they can be reused
  // both by the Day/Week Sheet below and by the All-view edit panel (which
  // renders the same Sheet for a single workout). Behaviour is unchanged.
  const onCopy = (data: readonly BlockSelectionData[]) => {
    const groupedData = groupSelectionData(data);
    if (groupedData.length === 0) return;
    const days = groupedData.map((wgd) => {
      const workout = workouts.find((w) => w.workoutId === wgd.workoutId);
      return { assignedDate: workout?.assignedDate ?? null, data: wgd };
    });
    setWorkoutClipboard(buildWorkoutClipboard(days));
  };

  const onPaste = (_: undefined, targetData: readonly BlockSelectionData[]) => {
    const workoutClipboard = jotaiStore.get(workoutClipboardAtom);
    if (!workoutClipboard) return;
    const groupedTargetData = groupSelectionData(targetData);
    if (workoutClipboard.days.length > 1) {
      // Multi-day paste: map clipboard days to target workouts by date offset
      const firstTarget = groupedTargetData[0];
      if (!firstTarget) return;
      const result = collectMultiDayPasteCommandsByPosition(
        workoutClipboard,
        firstTarget.workoutId,
        workouts,
        'AthleteDraftWorkoutLayer',
        athleteWorkoutLayers,
        setsMetric,
        blockWorkoutLayers,
        groupBy,
        firstTarget,
      );
      if (!result.ok) {
        toast({ title: result.error, severity: 'error' });
      } else if (result.value.length > 0) {
        runCommands(result.value);
      }
    } else {
      // Single-day paste: paste directly to the selected cells
      const clipboardData = workoutClipboard.days[0]?.data;
      if (!clipboardData) return;
      const result = pasteCommands(
        'AthleteDraftWorkoutLayer',
        athleteWorkoutLayers,
        clipboardData,
        groupedTargetData,
        setsMetric,
        blockWorkoutLayers,
      );
      if (result.ok) {
        runCommands(result.value);
      } else {
        toast({ title: result.error, severity: 'error' });
      }
    }
  };

  const onBulkClear = (rawData: readonly BlockSelectionData[]) => {
    const groupedData = groupSelectionData(rawData);
    const result = commandsForBulkClear(
      'AthleteDraftWorkoutLayer',
      athleteWorkoutLayers,
      groupedData,
    );
    if (result.ok) {
      runCommands(result.value);
    } else {
      toast({
        title: result.error,
        severity: 'error',
      });
    }
  };

  const onDragEnd = (event: DragEndEvent) =>
    handleDragEnd(event, workouts, mutationFns);

  if (groupBy !== 'all' && (!currentExerciseBenchmarks || !maxConversions)) {
    return null;
  }

  const applyBenchmarkData: MetricBenchmarkData = {
    currentExerciseBenchmarks: currentExerciseBenchmarks ?? [],
    maxConversions: maxConversions ?? [],
    trainingMaxRatio: block.trainingMaxRatio,
    defaultBenchmarkGridId,
    kpiBenchmarks: kpiBenchmarks ?? {},
  };
  const editContext: SingleWorkoutEditContext = {
    allErrors: workoutPublishErrors,
    mutationFns,
    miscMutationFns,
    applyBenchmarkData,
    hasBlockBeenPublished,
    isPublishDisabled,
    blockStatus: block.status,
    allowFlexWorkouts: block.allowFlexWorkouts,
    selectedAthleteId: selectedAthlete.id,
    showActuals,
    forceReadOnly: readOnly,
    getAssignedDate,
    onUndo: undo,
    onRedo: redo,
    onCopy,
    onPaste,
    onBulkClear,
    onDragEnd,
    addWorkout:
      readOnly || showActuals
        ? undefined
        : { create: createNewDraftWorkout, cancel: cancelNewDraftWorkout },
    getContextMenuContent: readOnly
      ? null
      : (data, fns) => {
          const groupedData = groupSelectionData(data);
          return getContextMenuContent(
            groupedData,
            getMetric,
            metrics,
            athleteWorkoutLayers,
            mutationFns,
            runCommands,
            setInstructionEditData,
            onSetWorkoutTemplate,
            setNewSchemeData,
            t,
            'AthleteDraftWorkoutLayer',
            brandColors,
            fns,
            defaultBenchmarkGridId,
            canCreateScheme,
            canCreateTemplate,
            allViewWorkouts,
            undefined,
            blockWorkoutLayers,
            'all',
            hasBlockBeenPublished,
            onSetMultiWorkoutTemplate,
          );
        },
    onDeleteWorkout: (workoutId: string) => {
      const workout = allViewWorkouts.find((w) => w.workoutId === workoutId);
      if (workout) {
        if (!workout.exists) {
          cancelNewDraftWorkout();
        } else {
          doSessionDelete({
            cycleSortOrder: workout.cycle.id,
            sessionSortOrder: workout.session.id,
          });
        }
      }
    },
    onCancelWorkout: doWorkoutCancel,
  };

  const handleTrackExerciseSwap = (info: TrackExerciseSwapInfo) => {
    const hasFutureInstances =
      findMatchingExercisesInFutureWorkouts(
        info.workoutId,
        info.oldLibraryExerciseId,
        workouts,
      ).length > 0;

    // If allAthletes hasn't loaded yet (blockForDraftQuery still in flight),
    // be optimistic — we're on the athlete layer so the block has other
    // athletes. The real value is computed lazily when the drawer opens via
    // the applySwapMatchingAthletes useMemo.
    let hasMatchingAthletes: boolean;
    if (allAthletes.length === 0) {
      hasMatchingAthletes = true;
    } else {
      const matchingAthletes = findMatchingAthletesForSwap(
        allAthletes,
        blockWorkoutLayers,
        allAthleteDraftWorkoutLayers,
        profileId,
        info.oldLibraryExerciseId,
      );
      hasMatchingAthletes = matchingAthletes.some((a) => a.hasMatch);
    }

    if (!hasFutureInstances && !hasMatchingAthletes) return;

    const swapData: ApplySwapData = {
      ...info,
      isBlockLayer: false,
      hasFutureInstances,
      hasMatchingAthletes,
    };

    jotaiStore.set(
      recentSwapsAtom,
      addRecentSwap(jotaiStore.get(recentSwapsAtom), swapData),
    );
  };

  const handleApplySwapSave = (options: {
    applyToFutureInstances: boolean;
    applyToOtherAthletes: boolean;
    selectedAthleteIds: string[];
  }) => {
    if (!applySwapData) return;

    const athleteCount = options.selectedAthleteIds.length;

    // Apply to future instances for the current athlete
    if (options.applyToFutureInstances) {
      const matchingExercises = findMatchingExercisesInFutureWorkouts(
        applySwapData.workoutId,
        applySwapData.oldLibraryExerciseId,
        workouts,
      );
      if (matchingExercises.length > 0) {
        const swapCommands = generateSwapCommandsForFutureInstances(
          matchingExercises,
          applySwapData.newLibraryExerciseId,
          applySwapData.newExerciseName,
          'AthleteDraftWorkoutLayer',
          athleteWorkoutLayers as HasWorkoutLayer[],
          blockWorkoutLayers as HasWorkoutLayer[],
        );
        runCommands(swapCommands);
      }
    }

    // Apply to other athletes
    if (options.applyToOtherAthletes && athleteCount > 0) {
      for (const athleteId of options.selectedAthleteIds) {
        const theirLayers = allAthleteDraftWorkoutLayers.filter(
          (l) => l.profileId === athleteId,
        );

        // Search both block and athlete layers for this specific athlete
        let matchingExercises = findMatchingExercisesForAthlete(
          blockWorkoutLayers,
          theirLayers,
          applySwapData.oldLibraryExerciseId,
        );

        // Without "apply to future instances", only swap the current workout
        if (!options.applyToFutureInstances) {
          matchingExercises = matchingExercises.filter(
            (m) => m.workoutId === applySwapData.workoutId,
          );
        }

        const swapCommands = generateSwapCommandsForFutureInstances(
          matchingExercises,
          applySwapData.newLibraryExerciseId,
          applySwapData.newExerciseName,
          'AthleteDraftWorkoutLayer',
          theirLayers as HasWorkoutLayer[],
          blockWorkoutLayers as HasWorkoutLayer[],
        );

        // Convert to domain commands and mutate for this athlete
        for (const command of swapCommands) {
          const edits = command.edits as AthleteDraftWorkoutDomain.Edit[];
          const { exists, workoutId, id } = command.identity;

          if (!exists) {
            // SC-5512: slot from the cell (empty-slot keys encode it), never
            // a silent slot-0 fallback.
            const {
              cycleSortOrder,
              sessionSortOrder,
              match: matchWorkout,
            } = resolveCreateSlot(workoutId, workouts);
            // Ensure block layer exists for this workout
            const blockLayer = blockWorkoutLayers.find(
              (l) => l.workoutId === workoutId,
            );
            if (!blockLayer) {
              rawMutation.mutate({
                module: BlockDraftWorkoutDomain.Module,
                identity: { workoutId, blockId: block.id },
                edits: [
                  {
                    modelName: 'BlockDraftWorkoutLayer',
                    modelId: v4(),
                    action: 'create',
                    data: {
                      cycleSortOrder,
                      sessionSortOrder,
                      layer: WorkoutLayerDomain.defaultLayer(),
                      name: matchWorkout?.name ?? null,
                    },
                  },
                ],
              });
            }

            edits.unshift({
              modelName: 'AthleteDraftWorkoutLayer',
              modelId: id,
              action: 'create',
              data: {
                cycleSortOrder,
                sessionSortOrder,
                layer: WorkoutLayerDomain.defaultLayer(),
                name: matchWorkout?.name ?? null,
              },
            });
          }

          rawMutation.mutate({
            module: AthleteDraftWorkoutDomain.Module,
            identity: { workoutId, blockId: block.id, profileId: athleteId },
            edits,
          });
        }
      }
    }

    // Clear clipboard
    setWorkoutClipboard(null);

    // Show contextual toast
    if (options.applyToFutureInstances && options.applyToOtherAthletes) {
      toast({
        title: t('success'),
        description: t('exerciseChangeAppliedToFutureAndAthletes', {
          count: athleteCount,
        }),
        severity: 'success',
      });
    } else if (options.applyToFutureInstances) {
      toast({
        title: t('success'),
        description: t('exerciseChangeAppliedToFutureForAthlete'),
        severity: 'success',
      });
    } else if (options.applyToOtherAthletes) {
      toast({
        title: t('success'),
        description: t('exerciseChangeAppliedToAthletes', {
          count: athleteCount,
        }),
        severity: 'success',
      });
    }

    // Clean up swap tracking
    jotaiStore.set(
      recentSwapsAtom,
      removeRecentSwap(
        jotaiStore.get(recentSwapsAtom),
        applySwapData.exerciseId,
      ),
    );
    setApplySwapData(null);
  };

  const sheetProps = getSlotsForBlock({
    workouts,
    allErrors: workoutPublishErrors,
    view,
    mutationFns,
    miscMutationFns,
    applyBenchmarkData,
    setModalOrDrawerOpen,
    hasBlockBeenPublished,
    blockStatus: block.status,
    isPublishDisabled,
    allowFlexWorkouts: block.allowFlexWorkouts,
    getMetric,
    selectedWorkoutId,
    selectedAthleteId: selectedAthlete.id,
    showActuals,
    maxSetsColumns,
    maxNonSetsColumns,
    greatestExerciseHeightsInSections,
    getAssignedDate,
    applySwapCallbacks: {
      onOpenApplySwap: setApplySwapData,
      onTrackExerciseSwap: handleTrackExerciseSwap,
    },
    forceReadOnly: readOnly,
  });

  const gridTemplateColumns = getGridTemplateColumns(
    displayWorkouts,
    maxNonSetsColumns,
    maxMetricsPerExercise,
    block.status,
    showActuals || readOnly,
  );

  return (
    <>
      {groupBy === 'all' ? (
        <BlockAllView
          block={block}
          workouts={allViewWorkouts}
          applyBenchmarkData={applyBenchmarkData}
          editContext={editContext}
          overlayOpen={!!instructionEditData || !!workoutTemplateData}
        />
      ) : (
        <Sheet
          {...sheetProps}
          sensors={sheetSensors}
          gridTemplateColumns={gridTemplateColumns}
          className="overflow-auto"
          onUndo={undo}
          onRedo={redo}
          onCopy={onCopy}
          onPaste={onPaste}
          onBulkClear={onBulkClear}
          getContextMenuContent={(rawData) => {
            const groupedData = groupSelectionData(rawData);
            return getContextMenuContent(
              groupedData,
              getMetric,
              metrics,
              athleteWorkoutLayers,
              mutationFns,
              runCommands,
              setInstructionEditData,
              onSetWorkoutTemplate,
              setNewSchemeData,
              t,
              'AthleteDraftWorkoutLayer',
              brandColors,
              miscMutationFns,
              defaultBenchmarkGridId,
              canCreateScheme,
              canCreateTemplate,
              workouts,
              undefined,
              blockWorkoutLayers,
              groupBy,
              hasBlockBeenPublished,
              onSetMultiWorkoutTemplate,
            );
          }}
          readOnly={readOnly}
          onDragEnd={onDragEnd}
          disableKeyboardAndMouse={
            readOnly ||
            modalOrDrawerOpen ||
            !!instructionEditData ||
            !!newSchemeData ||
            !!applySwapData
          }
        />
      )}
      {instructionEditData && (
        <InstructionDrawer
          instruction={instructionEditData.instruction}
          setInstructionEditData={setInstructionEditData}
          editInstruction={(newInstruction) => {
            // if an exercise id is present, this is for an exercise instruction, otheriwse it's for a section
            if (instructionEditData.workoutExerciseId) {
              mutationFns.onEditExercise(
                instructionEditData.workoutId,
                instructionEditData.workoutExerciseId,
                {
                  instruction: newInstruction,
                },
              );
            } else {
              mutationFns.onEditSection(
                instructionEditData.workoutId,
                instructionEditData.workoutSectionId,
                {
                  instruction: newInstruction,
                },
              );
            }
          }}
          maxLength={instructionEditData.workoutExerciseId ? 300 : 1000}
        />
      )}
      {workoutTemplateData && (
        <WorkoutTemplateDrawer
          mode={'create'}
          workoutTemplate={workoutTemplateData}
          tagOptions={workoutTemplateTagOptions}
          onClose={() => setWorkoutTemplateData(null)}
          onSave={onSaveWorkoutTemplate}
          onDelete={() => {
            // no delete on create
          }}
        />
      )}
      {newSchemeData && (
        <NewSchemeDialog
          onCreateScheme={onCreateScheme}
          setsData={newSchemeData}
          focusOptions={focusOptions}
          onClose={() => setNewSchemeData(null)}
        />
      )}
      <DeleteConfirmationModal
        id={`${sessionToDelete?.cycleSortOrder}-${sessionToDelete?.sessionSortOrder}`}
        open={!!sessionToDelete}
        onClose={() => setSessionToDelete(null)}
        onDelete={() => {
          setSessionToDelete(null);
          if (sessionToDelete) {
            doSessionDelete(sessionToDelete);
          }
        }}
        {...deleteAthleteWorkoutText(t)}
      />
      <DeleteConfirmationModal
        id={workoutToCancel || ''}
        open={!!workoutToCancel}
        onClose={() => setWorkoutToCancel(null)}
        onDelete={() => {
          setWorkoutToCancel(null);
          if (workoutToCancel) {
            doWorkoutCancel(workoutToCancel);
          }
        }}
        title={t('cancelWorkout')}
        message={getCancelWorkoutMessage(
          t,
          workouts.find((w) => w.workoutId === workoutToCancel),
          selectedAthlete,
        )}
        confirmText={t('yesCancel')}
        cancelText={t('close')}
      />

      <DeleteConfirmationModal
        id={workoutToDelete || ''}
        open={!!workoutToDelete}
        onClose={() => setWorkoutToDelete(null)}
        onDelete={() => {
          setWorkoutToDelete(null);
          if (workoutToDelete) {
            const workout = workouts.find(
              (w) => w.workoutId === workoutToDelete,
            );
            if (workout) {
              doSessionDelete({
                cycleSortOrder: workout.cycle.id,
                sessionSortOrder: workout.session.id,
              });
              toast({
                title: t('success'),
                description: t('workoutDeleted'),
                severity: 'success',
              });
            }
          }
        }}
        {...deleteAthleteWorkoutText(t)}
      />

      {applySwapData && (
        <ApplySwapDrawer
          data={applySwapData}
          matchingAthletes={applySwapMatchingAthletes}
          onClose={() => setApplySwapData(null)}
          onSave={handleApplySwapSave}
        />
      )}

      {pendingMultiApply?.warning === 'overflow' && (
        <ConfirmationModal
          id="apply-multi-workout-template-overflow-athlete"
          open
          onConfirm={confirmPendingMultiApply}
          onCancel={() => setPendingMultiApply(null)}
          confirmText={t('continue')}
          {...applyMultiWorkoutTemplateOverflowText(
            t,
            pendingMultiApply.template.name,
            pendingMultiApply.template.template.length,
            pendingMultiApply.plan.dayTargets.length,
          )}
        />
      )}
      {pendingMultiApply?.warning === 'actuals' && (
        <ConfirmationModal
          id="apply-multi-workout-template-actuals-athlete"
          open
          onConfirm={confirmPendingMultiApply}
          confirmText={t('ok')}
          {...applyMultiWorkoutTemplateActualsText(
            t,
            pendingMultiApply.plan.actualsDayNumbers,
          )}
        />
      )}
      {pendingMultiApply?.warning === 'draftOverwrite' && (
        <ConfirmationModal
          id="apply-multi-workout-template-draft-overwrite-athlete"
          open
          onConfirm={confirmPendingMultiApply}
          onCancel={() => setPendingMultiApply(null)}
          confirmText={t('continue')}
          {...applyMultiWorkoutTemplateDraftOverwriteText(
            t,
            pendingMultiApply.plan.draftWorkoutIds.length,
          )}
        />
      )}
    </>
  );
}

function getCancelWorkoutMessage(
  t: TFunction<'translation'>,
  workout: WorkoutWithCycleAndSession | undefined,
  selectedAthlete: AthletePreview,
) {
  if (!workout) {
    return '';
  }
  return t('willBeCanceledFor', {
    workout: getDisplayNameForWorkout(workout),
    athlete: athleteDisplayName(selectedAthlete),
  });
}
